// ═══════════════════════════════════════════════════════════════
// RollCall — Add Personnel
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Paper, Group, Button, TextInput, Select, Textarea,
} from '@mantine/core';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useRollCallStore } from '../../store/rollcallStore';
import { ICS_POSITIONS } from '../../types';

export default function PersonnelCreate() {
  const navigate = useNavigate();
  const { personnel, loadPersonnel, createPersonnel } = useRollCallStore();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', agency: '', position: '',
    pin: '', badgeId: '', phone: '', email: '', notes: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => { loadPersonnel(); }, []);

  const update = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!form.agency.trim()) errs.agency = 'Required';
    if (!form.pin.trim()) errs.pin = 'Required';
    else if (!/^\d{4,6}$/.test(form.pin)) errs.pin = 'PIN must be 4-6 digits';
    else if (personnel.some(p => p.pin === form.pin)) errs.pin = 'PIN already in use';
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      notifications.show({ title: 'Error', message: 'Please fix validation errors', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      await createPersonnel({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        agency: form.agency.trim(),
        position: form.position || '',
        pin: form.pin,
        badgeId: form.badgeId.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        notes: form.notes.trim(),
      });
      notifications.show({ title: 'Added', message: 'Personnel added to roster', color: 'green' });
      navigate('/rollcall/personnel');
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err?.message || 'Failed to add', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Add Personnel</Title>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/rollcall/personnel')}>Cancel</Button>
      </Group>

      <Paper p="lg" radius="sm" withBorder>
        <Group grow mb="md">
          <TextInput label="First Name" required value={form.firstName} error={errors.firstName}
            onChange={e => update('firstName', e.currentTarget.value)} />
          <TextInput label="Last Name" required value={form.lastName} error={errors.lastName}
            onChange={e => update('lastName', e.currentTarget.value)} />
        </Group>

        <TextInput label="Agency / Organization" required value={form.agency} error={errors.agency}
          onChange={e => update('agency', e.currentTarget.value)} mb="md" />

        <Select label="ICS Position" placeholder="Select position" data={ICS_POSITIONS} clearable
          value={form.position || null} onChange={v => update('position', v ?? '')} mb="md" />

        <Group grow mb="md">
          <TextInput label="PIN" required placeholder="4-6 digits" value={form.pin} error={errors.pin}
            onChange={e => update('pin', e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
            description="Unique numeric PIN for kiosk check-in" />
          <TextInput label="Badge ID" placeholder="QR or barcode value" value={form.badgeId}
            onChange={e => update('badgeId', e.currentTarget.value)}
            description="Optional — for badge scanning" />
        </Group>

        <Group grow mb="md">
          <TextInput label="Phone" value={form.phone} onChange={e => update('phone', e.currentTarget.value)} />
          <TextInput label="Email" value={form.email} onChange={e => update('email', e.currentTarget.value)} />
        </Group>

        <Textarea label="Notes" value={form.notes} onChange={e => update('notes', e.currentTarget.value)} mb="lg" />

        <Group justify="flex-end">
          <Button variant="light" onClick={() => navigate('/rollcall/personnel')}>Cancel</Button>
          <Button onClick={handleSave} loading={saving} leftSection={<IconCheck size={14} />}>
            Add Personnel
          </Button>
        </Group>
      </Paper>
    </div>
  );
}
